import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { resolveNexusChatUrl } from '../utils/nexusChatUrl';

// Resolved by environment, never a localhost URL (see nexusChatUrl).
const NEXUSCHAT_URL = resolveNexusChatUrl();

const DashboardPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(true);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);

  // Check for access denied state from protected route redirects
  useEffect(() => {
    if (location.state?.accessDenied) {
      setAccessDeniedMessage(location.state.message || 'You do not have access to the requested resource.');
      // Clear the state so the message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Redirect to login if not authenticated, storing the intended destination
  useEffect(() => {
    if (!isAuthenticated) {
      // Store the current path so we can redirect back after login
      sessionStorage.setItem('redirectAfterLogin', '/dashboard');
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch trial status
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!isAuthenticated || !user) return;

      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';
        const response = await fetch(`${API_URL}/trial/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTrialStatus(data.data);
        }
      } catch (error) {
        console.error('Error fetching trial status:', error);
      } finally {
        setLoadingTrial(false);
      }
    };

    fetchTrialStatus();
  }, [isAuthenticated, user]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="dashboard-page">
      {/* Access Denied Alert Banner */}
      {accessDeniedMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
              <p className="text-red-700 dark:text-red-300 font-medium">
                {accessDeniedMessage}
              </p>
            </div>
            <button
              onClick={() => setAccessDeniedMessage(null)}
              className="text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors"
              aria-label="Dismiss message"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Welcome Message */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <div className="inline-block p-4 rounded-full bg-green-100 mb-4">
                <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              <span style={{ color: '#FFC000' }}>Welcome!</span> Login Successful
            </h1>

            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              You're now connected to the AgentNexus platform
            </p>

            {user?.email && (
              <p className="text-lg text-gray-600">
                Logged in as: <span className="font-semibold" style={{ color: '#FFC000' }}>{user.email}</span>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Your Platform Capabilities
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {/* Revenue Cycle Intelligence */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border-t-4 flex flex-col" style={{ borderTopColor: '#FFC000' }}>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 192, 0, 0.1)' }}>
                  <svg className="w-6 h-6" style={{ color: '#FFC000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                Revenue Cycle Intelligence
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm flex-grow">
                Identify causes of increases in DNFB and DNSP metrics with advanced analytics.
              </p>

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded whitespace-nowrap">Coming Soon</span>
              </div>
              <div className="text-right mt-2">
                <Link
                  to="/features/revenue-cycle"
                  className="text-sm font-medium hover:opacity-80 whitespace-nowrap"
                  style={{ color: '#FFC000' }}
                >
                  Learn more →
                </Link>
              </div>
            </div>

            {/* Claims Denial Prevention */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border-t-4 flex flex-col" style={{ borderTopColor: '#FFC000' }}>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 192, 0, 0.1)' }}>
                  <svg className="w-6 h-6" style={{ color: '#FFC000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                Claims Denial Prevention
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm flex-grow">
                AI-driven system to prevent claim denials before they occur and streamline appeals.
              </p>

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded whitespace-nowrap">Coming Soon</span>
              </div>
              <div className="text-right mt-2">
                <Link
                  to="/features/claims-denial"
                  className="text-sm font-medium hover:opacity-80 whitespace-nowrap"
                  style={{ color: '#FFC000' }}
                >
                  Learn more →
                </Link>
              </div>
            </div>

            {/* CDI Trial Access */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border-t-4 flex flex-col" style={{ borderTopColor: '#FFC000' }}>
              {/* Icon and Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 192, 0, 0.1)' }}>
                  <svg className="w-6 h-6" style={{ color: '#FFC000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {loadingTrial ? (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-600 text-gray-300">
                    Loading...
                  </span>
                ) : trialStatus && trialStatus.trial_status === 'active' ? (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                    In Progress
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                    Available
                  </span>
                )}
              </div>

              {/* Title and Description */}
              <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                CDI Trial Access
              </h3>

              {loadingTrial ? (
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm flex-grow">Loading trial status...</p>
              ) : trialStatus && trialStatus.trial_status === 'active' ? (
                <div className="flex-grow">
                  <p className="text-gray-600 dark:text-gray-400 mb-2 text-sm">
                    Clinical Documentation Improvement trial with AI-powered insights and automation.
                  </p>
                  <div className="mt-3 mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>Step {trialStatus.trial_current_step} of 5 - {trialStatus.step_name}</span>
                      <span>{trialStatus.progress_percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-300 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${trialStatus.progress_percentage}%`,
                          backgroundColor: '#FFC000'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-sm font-medium mb-3" style={{ color: '#FFC000' }}>
                    {trialStatus.days_remaining} days remaining in trial
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm flex-grow">
                  Clinical Documentation Improvement trial with AI-powered insights and automation.
                </p>
              )}

              {/* Action Link */}
              <div className="text-right">
                <Link
                  to={trialStatus && trialStatus.trial_status === 'active' ? trialStatus.next_step_url : '/cdi-trial'}
                  className="text-sm font-medium hover:opacity-80"
                  style={{ color: '#FFC000' }}
                >
                  {trialStatus && trialStatus.trial_status === 'active' ? 'Continue to Your Trial →' : 'Learn more →'}
                </Link>
              </div>
            </div>

            {/* Nex AI Assistant */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border-t-4 flex flex-col" style={{ borderTopColor: '#FFC000' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 192, 0, 0.1)' }}>
                  <svg className="w-6 h-6" style={{ color: '#FFC000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                  Available
                </span>
              </div>

              <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                Nex AI Assistant
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm flex-grow">
                Ask questions about your claims, CDI results, and coding guidelines. All capability is driven thru Nex.
              </p>

              <div className="text-right">
                <a
                  href={NEXUSCHAT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ color: '#FFC000' }}
                >
                  Launch Nex →
                </a>
              </div>
            </div>

            {/* Analytics Dashboard */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border-t-4 flex flex-col" style={{ borderTopColor: '#FFC000' }}>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 192, 0, 0.1)' }}>
                  <svg className="w-6 h-6" style={{ color: '#FFC000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                Analytics Dashboard
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm flex-grow">
                Comprehensive analytics and reporting for data-driven decision making.
              </p>

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded whitespace-nowrap">Coming Soon</span>
              </div>
              <div className="text-right mt-2">
                <Link
                  to="/analytics"
                  className="text-sm font-medium hover:opacity-80 whitespace-nowrap"
                  style={{ color: '#FFC000' }}
                >
                  Learn more →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="py-16" style={{ backgroundColor: '#0E3056' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready to Explore More Features?
            </h2>

            <p className="text-xl mb-8 text-gray-200">
              Discover how AgentNexus can transform your healthcare operations with AI-powered insights and automation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/features"
                style={{
                  backgroundColor: '#FFC000',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:opacity-90 inline-block"
              >
                Explore Features
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #FFC000',
                  color: '#FFC000',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:bg-[#FFC000] hover:text-[#0E3056] inline-block"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
